<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button
            class="backButton"
            @click="onBackButtonClick"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.managementTarget.back')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('gpsTracker.managementTarget.title') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- データ -->
    <section class="tti-padding-content">
      <div class="tti-content-title company-content">
        <div class="company-content-code">
          <span>{{ $t('gpsTracker.managementTarget.companyCode') }}</span>
          <el-input v-model.trim="companyCode" disabled="disabled"></el-input>
        </div>
        <div class="company-content-name">
          <span>{{ $t('gpsTracker.managementTarget.companyName') }}</span>
          <el-input v-model.trim="companyName" disabled="disabled"></el-input>
        </div>
        <div class="searchBox">
          <el-input
            v-model.trim="searchText"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.managementTarget.tbCompanyCode')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          ></el-input>
          <!-- 検索 -->
          <el-button
            class="tti-btn-default tti-btn-002"
            @click="onSearchBoxClick"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.managementTarget.btnSearch')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            {{ $t('buttons.search') }}
          </el-button>
          <!-- 全表示 -->
          <el-button
            class="tti-btn-default tti-btn-002"
            @click="onAllDisplayClick"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.managementTarget.btnViewAll')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            {{ $t('buttons.allDisplay') }}
          </el-button>
        </div>
      </div>
      <el-table
        ref="tableRef"
        :data="tableData"
        height="calc(100vh - 200px)"
        :border="true"
        :stripe="true"
        header-row-class-name="tti-master-table-header"
        :empty-text="$t('messages.common.emptyTableData')"
        highlight-current-row
        @current-change="handleCurrentChange"
      >
        <!-- 管理対象コード -->
        <el-table-column
          prop="managementTargetCode"
          :label="$t('gpsTracker.managementTarget.managementTargetCode')"
          width="130"
        >
        </el-table-column>
        <!-- 管理対象名 -->
        <el-table-column
          prop="managementTargetName"
          :label="$t('gpsTracker.managementTarget.managementTargetName')"
          width="200"
        >
        </el-table-column>
        <!-- 営業所code -->
        <el-table-column
          prop="businessOfficeCode"
          :label="$t('gpsTracker.managementTarget.businessOfficeCode')"
          width="130"
        >
        </el-table-column>
        <!-- 営業所名 -->
        <el-table-column
          prop="businessOfficeName"
          :label="$t('gpsTracker.managementTarget.businessOfficeName')"
          width="200"
        >
        </el-table-column>
        <!-- GPSトラッカーID -->
        <el-table-column prop="sigfoxId" :label="$t('gpsTracker.managementTarget.sigfoxId')" width="180">
        </el-table-column>
        <!-- 利用状況 -->
        <el-table-column prop="usageStatus" :label="$t('gpsTracker.managementTarget.usageStatus')" width="80">
          <template slot-scope="scope">
            {{ getUsageStatusText(scope.row.usageStatus) }}
          </template>
        </el-table-column>
        <!-- 所有者 -->
        <el-table-column prop="owner" :label="$t('gpsTracker.managementTarget.owner')" width="160"> </el-table-column>
        <!-- 貸出先営業所code -->
        <el-table-column
          prop="lendBusinessOfficeCode"
          :label="$t('gpsTracker.managementTarget.lendBusinessOfficeCode')"
          width="160"
        >
        </el-table-column>
        <!-- 貸出先営業所名 -->
        <el-table-column
          prop="lendBusinessOfficeName"
          :label="$t('gpsTracker.managementTarget.lendBusinessOfficeName')"
          width="160"
        >
        </el-table-column>
        <!-- 貸出日 -->
        <el-table-column prop="lendDate" :label="$t('gpsTracker.managementTarget.lendDate')" width="160">
        </el-table-column>
        <!-- 次回定期整備期日 -->
        <el-table-column
          prop="nextArrangementDate"
          :label="$t('gpsTracker.managementTarget.nextArrangementDate')"
          width="150"
        >
        </el-table-column>
        <!-- 次回車検期日 -->
        <el-table-column prop="nextCheckDate" :label="$t('gpsTracker.managementTarget.nextCheckDate')" width="150">
        </el-table-column>
      </el-table>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref, Vue } from 'vue-property-decorator'
import { Table } from 'element-ui'

import { CompanyAddToolData, CompanyGeneral, ToolManagementTargetData, ToolManagementTargetGetParam } from '@/models'
import { getManagementTargetListData } from '@/store/apis'
import { companyGeneralService, CompanyGeneralType } from '@/commons/services/companyGeneralService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { i18n } from '@/locales'
import config from '@/config/config'

@Component({
  name: 'TOOL_MANAGEMENT_TARGET_MASTER'
})
export default class TOOL_MANAGEMENT_TARGET_MASTER extends Vue {
  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画面ID
   */
  pageId = 'TOOL_MANAGEMENT_TARGET_MASTER'

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scope: any

  /**
   * 会社情報ID
   */
  companyId = ''

  /**
   * 検索文字列
   */
  searchText = ''

  /**
   * 変更したデータ
   */
  tableData: Array<ToolManagementTargetData> = []

  /**
   * テーブル行で選択したデータ
   */
  singleSelection: ToolManagementTargetData | undefined

  /**
   * 契約会社コード.
   */
  companyCode = ''
  /**
   * 契約会社名
   */
  companyName = ''

  /**
   * 利用状況
   */
  usageStatusList: CompanyGeneral[] = []

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('TOOL_MANAGEMENT_TARGET_MASTER > mounted')
    this.companyId = this.$route.query.companyId as string
    if (!this.routeQueryCheck()) {
      return
    }
    this.fetchUsageStatus()
    this.fetchTableData()
  }

  fetchTableDataBefore(): () => Promise<CompanyAddToolData> {
    return () => {
      const param = {
        companyId: this.companyId,
        managementTargetCodeOrName: this.searchText
      } as ToolManagementTargetGetParam
      return getManagementTargetListData(param)
    }
  }

  /**
   * テーブルデータを検索して、初期化を実行する。
   *
   * @returns {Promise<boolean>} 検索実行結果
   */
  async fetchTableData(): Promise<boolean> {
    try {
      this.$log.debug(`TOOL_USER_INFO > fetchTableData`)
      const data = (await this.fetchTableDataBefore()()) as CompanyAddToolData
      this.companyCode = data.companyCode as string
      this.companyName = data.companyName as string

      this.initTableData(data.list as Array<ToolManagementTargetData>)
      return true
      /* eslint-disable @typescript-eslint/no-explicit-any */
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    } catch (e: any) {
      if (e?.data?.common?.code === 'E00101') {
        this.$router.push(config.toolMenu.companyInfo)
        return false
      }
      this.initTableData([])
      this.$log.debug(`TOOL_MANAGEMENT_TARGET_MASTER > fetchTableData: e=`, e)
      throw e
    }
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @param {Array<ToolManagementTargetData>} data 検索されたデータ
   * @returns {void}
   */
  initTableData(data: Array<ToolManagementTargetData>): void {
    if (!data) {
      data = []
    }
    this.tableData = data
  }

  /**
   * パラメータ着信がない場合
   *
   * @returns {boolean} 関連結菓
   */
  routeQueryCheck(): boolean {
    if (!this.companyId) {
      msgBoxService.info(i18n.t('vaildate.common.E00092').toString(), () => {
        this.$router.push(config.toolMenu.companyInfo)
      })
      return false
    }
    return true
  }

  /**
   * 検索BOXをクリックしたときの処理
   *
   * @returns {void}
   */
  onSearchBoxClick(): void {
    this.fetchTableData()
  }

  /**
   * 全表示
   *
   */
  onAllDisplayClick(): void {
    this.$log.debug('TOOL_MANAGEMENT_TARGET_MASTER > onAllDisplayClick ')
    this.searchText = ''
    this.fetchTableData()
  }

  /**
   * テーブルの選択したデータを変更する。
   *
   * @param {ToolManagementTargetData} row 選択されたデータ
   */
  handleCurrentChange(row: ToolManagementTargetData): void {
    this.$log.debug(`TOOL_MANAGEMENT_TARGET_MASTER > handleCurrentChange,`, row)
    this.singleSelection = row
  }

  /**
   * 戻るボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onBackButtonClick(): void {
    this.$log.debug(`TOOL_MANAGEMENT_TARGET_MASTER > onBackButtonClick`)
    this.doBack()
  }

  /**
   * 前画面へ戻る
   *
   * @returns {void}
   */
  doBack(): void {
    this.$log.debug(`TOOL_MANAGEMENT_TARGET_MASTER > doBack`)
    this.$router.back()
  }

  /**
   * 利用状況nameを取得する。
   *
   * @param {string} role 利用状況code
   * @returns {string} 利用状況name
   */
  getUsageStatusText(role: string): string {
    if (!this.usageStatusList || this.usageStatusList.length === 0) return ''

    const usageStatus = this.usageStatusList.filter(item => item.code === role)[0]
    if (!usageStatus) return ''
    return usageStatus.name
  }

  /**
   * 利用状況リストを取得する
   *
   * @returns {void}
   */
  async fetchUsageStatus(): Promise<void> {
    try {
      this.usageStatusList = await companyGeneralService[this.companyId][CompanyGeneralType.USAGE_STATUS]
    } catch (e) {
      this.$log.debug('MST_USER > fetchUsageStatus: e=', e)
      throw e
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-main-header-shadow {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.company-content {
  display: flex;
  align-items: center;
  column-gap: 50px;
  margin-bottom: 20px;

  .company-content-code {
    display: flex;
    align-items: center;
    column-gap: 50px;

    span {
      width: 180px;
    }
  }

  .company-content-name {
    display: flex;
    align-items: center;
    column-gap: 50px;

    span {
      width: 180px;
    }
  }

  .searchBox {
    display: flex;
    align-items: center;
  }

  ::v-deep .el-input__inner {
    height: 35px;
    line-height: 35px;
    padding: 0 8px;
  }
}
</style>
